import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => setMenuOpen(false);

  return (
    <section className="relative min-h-screen w-full overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f7645ac1ef2a9f446f62df3f6bc71253.jpg"
          alt="背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-slate-800/70"></div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-px h-96 bg-gradient-to-b from-amber-400/40 via-amber-400/20 to-transparent"></div>
        <div className="absolute top-32 right-1/3 w-px h-64 bg-gradient-to-b from-amber-400/30 via-amber-400/10 to-transparent"></div>
        <div className="absolute top-20 right-20 w-96 h-96 border border-white/5 rounded-full"></div>
        <div className="absolute top-32 right-32 w-72 h-72 border border-amber-400/10 rounded-full"></div>
        <div className="absolute -bottom-20 -left-20 w-80 h-80 border border-white/5 rounded-full"></div>
        <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-amber-400/60 rounded-full animate-pulse"></div>
        <div className="absolute top-1/3 right-1/5 w-1.5 h-1.5 bg-amber-400/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-2 h-2 bg-amber-400/50 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-slate-900/5' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 md:py-5 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/d8b069ddd48bc477a2a24842dd85a982/329b63b1e93dd762fd884ab9d568547a.png"
              alt="Jon&G"
              className={`h-7 md:h-8 transition-all duration-500 ${scrolled ? 'brightness-100' : 'brightness-0 invert'}`}
            />
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            <a href="#services" className={`relative text-sm font-medium transition-colors cursor-pointer whitespace-nowrap group ${scrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'}`}>
              事業内容
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-400 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="#team" className={`relative text-sm font-medium transition-colors cursor-pointer whitespace-nowrap group ${scrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'}`}>
              メンバー紹介
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-400 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="#company" className={`relative text-sm font-medium transition-colors cursor-pointer whitespace-nowrap group ${scrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'}`}>
              会社情報
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-400 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="#contact" className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${scrolled ? 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg' : 'bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 hover:from-amber-300 hover:to-amber-400 shadow-lg shadow-amber-400/20'}`}>
              お問い合わせ
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="メニュー"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''} ${scrolled ? 'bg-slate-800' : 'bg-white'}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''} ${scrolled ? 'bg-slate-800' : 'bg-white'}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''} ${scrolled ? 'bg-slate-800' : 'bg-white'}`}></span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden transition-all duration-300 overflow-hidden ${menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'} bg-white/98 backdrop-blur-md border-t border-slate-100`}>
          <div className="px-5 py-4 flex flex-col gap-1">
            <a href="#services" onClick={handleNavClick} className="py-3 text-sm font-medium text-slate-700 hover:text-amber-600 border-b border-slate-100 cursor-pointer">事業内容</a>
            <a href="#team" onClick={handleNavClick} className="py-3 text-sm font-medium text-slate-700 hover:text-amber-600 border-b border-slate-100 cursor-pointer">メンバー紹介</a>
            <a href="#company" onClick={handleNavClick} className="py-3 text-sm font-medium text-slate-700 hover:text-amber-600 border-b border-slate-100 cursor-pointer">会社情報</a>
            <a href="#contact" onClick={handleNavClick} className="mt-2 py-3 px-4 bg-slate-900 text-white text-sm font-medium rounded-md text-center cursor-pointer">お問い合わせ</a>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-5 md:px-8 w-full">
          <div className="max-w-4xl">
            <div className="mb-6 md:mb-8">
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white leading-tight mb-6 md:mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                人と組織の可能性を、<br />
                <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300 bg-clip-text text-transparent">最大化する。</span>
              </h1>
            </div>

            <div className="mb-8 md:mb-12 relative pl-4 md:pl-6 border-l-2 border-amber-400/40">
              <div className="space-y-2 md:space-y-3 text-white/80 text-sm md:text-base leading-relaxed max-w-2xl">
                <p>私たちは、人間の可能性を信じている。</p>
                <p>だからこそ、戦略を語るだけのコンサルティングはしない。</p>
                <p className="text-white/60 text-xs md:text-sm">人事組織の変革、財務の強化、営業の本質的な仕組み化、そしてAIという武器の提供。<br className="hidden sm:block" />私たちが提供するすべての支援は、人と組織が本来持っている力を引き出し、未来を切り拓くためにある。</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-5">
              <a
                href="#contact"
                className="group inline-flex items-center justify-center gap-3 md:gap-4 px-6 md:px-8 py-3.5 md:py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all font-semibold whitespace-nowrap cursor-pointer shadow-xl shadow-amber-400/20 hover:shadow-amber-400/30 hover:-translate-y-0.5 text-sm md:text-base"
              >
                <span>無料相談を申し込む</span>
                <div className="w-7 h-7 md:w-8 md:h-8 bg-slate-900/10 rounded-full flex items-center justify-center group-hover:bg-slate-900/20 transition-colors">
                  <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3.5 md:py-4 border border-white/30 text-white rounded-lg hover:bg-white/10 hover:border-white/50 transition-all font-medium whitespace-nowrap cursor-pointer backdrop-blur-sm text-sm md:text-base"
              >
                <span>事業内容を見る</span>
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <span className="text-white/40 text-xs tracking-widest">SCROLL</span>
        <div className="w-6 h-10 border border-white/20 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-amber-400 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}
